import { Suspense, lazy, useEffect, useState, type ReactNode } from "react";
import { Nav } from "./components/Nav";
import { ErrorBoundary } from "./components/ErrorBoundary";
import { Toaster } from "./components/Toaster";
import { ConfirmHost } from "./components/ConfirmModal";
import { PhotoViewer } from "./components/PhotoViewer";
import { AuthGate } from "./components/AuthGate";
import { LegacyRecovery } from "./components/LegacyRecovery";
// Always-loaded pages (the core flow). The rest are lazy-loaded below so
// the first-paint bundle stays small on cellular.
import { HomePage } from "./pages/HomePage";
import { CollectionsPage } from "./pages/CollectionsPage";

// Lazy chunks. Each becomes its own JS file that downloads on first visit
// and is then cached. Each page module exports a named component, so we
// adapt with .then(m => ({ default: m.X })).
const FeedbackPage = lazy(() =>
  import("./pages/FeedbackPage").then((m) => ({ default: m.FeedbackPage })),
);
const BillingPage = lazy(() =>
  import("./pages/BillingPage").then((m) => ({ default: m.BillingPage })),
);
const AccountPage = lazy(() =>
  import("./pages/AccountPage").then((m) => ({ default: m.AccountPage })),
);
const ImportPage = lazy(() =>
  import("./pages/ImportPage").then((m) => ({ default: m.ImportPage })),
);
const AdminPage = lazy(() =>
  import("./pages/AdminPage").then((m) => ({ default: m.AdminPage })),
);
const PrivacyPage = lazy(() =>
  import("./pages/PrivacyPage").then((m) => ({ default: m.PrivacyPage })),
);
const TermsPage = lazy(() =>
  import("./pages/TermsPage").then((m) => ({ default: m.TermsPage })),
);
const CookiesPage = lazy(() =>
  import("./pages/CookiesPage").then((m) => ({ default: m.CookiesPage })),
);
const RefundsPage = lazy(() =>
  import("./pages/RefundsPage").then((m) => ({ default: m.RefundsPage })),
);
const ForgotPasswordPage = lazy(() =>
  import("./pages/ForgotPasswordPage").then((m) => ({
    default: m.ForgotPasswordPage,
  })),
);
const ResetPasswordPage = lazy(() =>
  import("./pages/ResetPasswordPage").then((m) => ({
    default: m.ResetPasswordPage,
  })),
);
const VerifyEmailPage = lazy(() =>
  import("./pages/VerifyEmailPage").then((m) => ({
    default: m.VerifyEmailPage,
  })),
);
const SharedTripPage = lazy(() =>
  import("./pages/SharedTripPage").then((m) => ({ default: m.SharedTripPage })),
);
const DebugPage = lazy(() =>
  import("./pages/DebugPage").then((m) => ({ default: m.DebugPage })),
);
const BuddyCollectionPage = lazy(() =>
  import("./pages/BuddyCollectionPage").then((m) => ({
    default: m.BuddyCollectionPage,
  })),
);
const PlanPage = lazy(() =>
  import("./pages/PlanPage").then((m) => ({ default: m.PlanPage })),
);
const PlanLanding = lazy(() =>
  import("./pages/PlanPage").then((m) => ({ default: m.PlanLanding })),
);

import { useRoute } from "./lib/router";
import {
  prepareAccount,
  backfillPlaceNames,
  backfillCommonNames,
} from "./lib/storage";
import { useAuth, refreshAuth } from "./lib/auth";
import { refreshBilling } from "./lib/billing";
import { loadConfig } from "./lib/config";
import { loadDistanceUnit } from "./lib/units";
import {
  startNotificationPolling,
  stopNotificationPolling,
} from "./lib/notifications";

// Routes that work without a signed-in account: legal docs (Privacy, Terms,
// Cookies, Refunds), and the unauthenticated parts of the auth lifecycle
// (forgot/reset password, email verification landing). Anything else
// requires sign-in and falls through to <AuthGate />.
const PUBLIC_ROUTES = new Set([
  "/privacy",
  "/terms",
  "/cookies",
  "/refunds",
  "/forgot-password",
  "/reset-password",
  "/verify-email",
]);

function renderPublic(route: string): ReactNode {
  switch (route) {
    case "/privacy":
      return <PrivacyPage />;
    case "/terms":
      return <TermsPage />;
    case "/cookies":
      return <CookiesPage />;
    case "/refunds":
      return <RefundsPage />;
    case "/forgot-password":
      return <ForgotPasswordPage />;
    case "/reset-password":
      return <ResetPasswordPage />;
    case "/verify-email":
      return <VerifyEmailPage />;
    default:
      // Public trip URLs — /t/:slug
      if (route.startsWith("/t/")) return <SharedTripPage />;
      return null;
  }
}

function PageLoader() {
  return (
    <div className="flex min-h-[40dvh] items-center justify-center text-sm text-ink/45">
      Loading…
    </div>
  );
}

export function App() {
  const route = useRoute();
  const auth = useAuth();

  useEffect(() => {
    void refreshAuth();
    void loadConfig();
  }, []);

  useEffect(() => {
    if (auth.status !== "authed") return;
    // Switch the local store to this account (wiping any other account's
    // residue), pull its durable copy, then clean up legacy place names.
    void prepareAccount(auth.email).then(() => {
      void backfillPlaceNames();
      void backfillCommonNames();
    });
    void refreshBilling();
    void loadDistanceUnit();
    startNotificationPolling();
    return () => {
      stopNotificationPolling();
    };
  }, [auth.status, auth.status === "authed" ? auth.email : null]);

  // Public routes render whether or not the user is signed in.
  if (PUBLIC_ROUTES.has(route) || route.startsWith("/t/")) {
    const view = renderPublic(route);
    if (view) {
      return <Suspense fallback={<PageLoader />}>{view}</Suspense>;
    }
  }

  if (auth.status === "loading") {
    return (
      <div className="flex min-h-[100dvh] items-center justify-center bg-paper text-sm text-ink/45">
        Loading…
      </div>
    );
  }

  if (auth.status === "anon") {
    return <AuthGate />;
  }

  return (
    <div className="flex min-h-[100dvh] flex-col pb-[calc(4rem+env(safe-area-inset-bottom))] sm:pb-0">
      <Nav />
      <OfflineBanner />
      <LegacyRecovery />
      <main className="flex-1">
        {/* Keyed by route so a crashed screen (a page or a modal it renders)
            shows a recoverable card while Nav/footer stay usable, and
            navigating to another tab resets the boundary automatically. */}
        <ErrorBoundary key={route}>
        <Suspense fallback={<PageLoader />}>
          {route === "/account" ? (
            <AccountPage />
          ) : route === "/admin" && auth.isAdmin ? (
            <AdminPage />
          ) : route === "/import" ? (
            <ImportPage />
          ) : route === "/billing" ? (
            <BillingPage />
          ) : route === "/feedback" ? (
            <FeedbackPage />
          ) : route === "/debug" ? (
            <DebugPage />
          ) : route.startsWith("/buddies/") ? (
            <BuddyCollectionPage
              userId={decodeURIComponent(route.slice("/buddies/".length))}
            />
          ) : route === "/plan" ? (
            <PlanLanding />
          ) : route.startsWith("/plan/") ? (
            <PlanPage
              spotKey={decodeURIComponent(route.slice("/plan/".length))}
            />
          ) : route === "/collections" || route === "/lifelist" ? (
            <CollectionsPage />
          ) : (
            <HomePage />
          )}
        </Suspense>
        </ErrorBoundary>
      </main>
      <SiteFooter />
      <Toaster />
      <ConfirmHost />
      <PhotoViewer />
    </div>
  );
}

// Thin banner shown when the device goes offline. The app still works from
// its local cache (recent spots, lifelist), so this reassures rather than
// alarms. The SW + 24h search cache do the actual offline serving.
function OfflineBanner() {
  const [offline, setOffline] = useState(
    typeof navigator !== "undefined" && navigator.onLine === false,
  );
  useEffect(() => {
    const on = () => setOffline(false);
    const off = () => setOffline(true);
    window.addEventListener("online", on);
    window.addEventListener("offline", off);
    return () => {
      window.removeEventListener("online", on);
      window.removeEventListener("offline", off);
    };
  }, []);
  if (!offline) return null;
  return (
    <div className="bg-aqua/25 px-4 py-2 text-center text-sm font-semibold text-ink">
      You're offline — recent spots and your lifelist still work from cache.
    </div>
  );
}

function SiteFooter() {
  return (
    <footer className="border-t border-ink/5 py-6 text-center text-xs text-ink/55">
      <div className="mx-auto flex max-w-3xl flex-col gap-2 px-4">
        <div className="flex flex-wrap justify-center gap-x-4 gap-y-1">
          <a href="/privacy" className="hover:text-ink/60">
            Privacy
          </a>
          <a href="/terms" className="hover:text-ink/60">
            Terms
          </a>
          <a href="/cookies" className="hover:text-ink/60">
            Cookies
          </a>
          <a href="/refunds" className="hover:text-ink/60">
            Refunds
          </a>
        </div>
        <div>
          Species data from iNaturalist, GBIF &amp; OBIS · Geocoding by
          OpenStreetMap Nominatim · IDs by Workers AI
        </div>
      </div>
    </footer>
  );
}
